import { useRef, type ChangeEvent } from 'react';
import { Underline } from 'lucide-react';
import toast from 'react-hot-toast';
import { renderUnderline, toggleUnderline } from '../../lib/richText';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Multiline (textarea) vs single line (input). Default false. */
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Compact mode: U button sits inline to the right of the input (for option rows). */
  compact?: boolean;
  className?: string;
}

/**
 * Text field with a one-click "Underline" button that wraps the current
 * selection in <u>…</u> markup, plus a live preview when markup is present.
 * The markup renders as an underline in the mobile app (see apps/mobile RichText).
 */
export default function UnderlineField({
  value,
  onChange,
  multiline = false,
  rows = 2,
  placeholder,
  required,
  disabled = false,
  compact = false,
  className = '',
}: Props) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const apply = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start === end) {
      toast.error('Select the word(s) to underline first');
      return;
    }
    const res = toggleUnderline(value, start, end);
    onChange(res.value);
    // Restore focus + selection after React re-renders the controlled value.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(res.selectionStart, res.selectionEnd);
    });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange(e.target.value);

  // preventDefault on mousedown keeps the input's text selection alive while the button is clicked.
  const underlineButton = (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={apply}
      title="Underline selected text"
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Underline size={13} />
      {!compact && 'Underline'}
    </button>
  );

  const inputEl = multiline ? (
    <textarea
      ref={ref as React.RefObject<HTMLTextAreaElement>}
      value={value}
      onChange={handleChange}
      rows={rows}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className="input-field disabled:opacity-50 disabled:cursor-not-allowed"
    />
  ) : (
    <input
      ref={ref as React.RefObject<HTMLInputElement>}
      type="text"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className="input-field disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );

  const preview = value.includes('<u>') ? (
    <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-700 font-korean">
      <span className="mr-2 text-[10px] uppercase tracking-wide text-gray-400">Preview</span>
      {renderUnderline(value)}
    </div>
  ) : null;

  if (compact) {
    return (
      <div className={`flex-1 min-w-0 ${className}`}>
        <div className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0">{inputEl}</div>
          {underlineButton}
        </div>
        {preview}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-1 flex items-center gap-2">{underlineButton}</div>
      {inputEl}
      {preview}
    </div>
  );
}
